/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState, useMemo} from 'react';

import DisplayModal from '../../../../../components/DisplayModal/DisplayModal';
import DisposalDisplay from '../../../../../components/DisplayModal/WasteDisplay/DisposalDisplay';
import {objTypes} from '../../../../../components/DrawerForm/Constants/commonConstants';
import AllDataInfo from '../../../../../components/DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../../../../../components/DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../../../../../components/Forms/Constants/IP/commonKeyLabel';
import {FacilityKey} from '../../../../../components/Forms/Constants/IP/FacilityKeyLabel';
import {
  EoLTKey,
  EoLTLabel
} from '../../../../../components/Forms/Constants/Products/EotKeyLabel';
import {RawMaterialLabel} from '../../../../../components/Forms/Constants/Products/RawMaterialKeyAndLabel';
import EoLTForm from '../../../../../components/Forms/EoLForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../../../../../components/Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../../../../../components/Jobs/JobsModal';
import Loader from '../../../../../components/Loader/Loader';
import {
  FullInfo,
  Title
} from '../../../../../components/Table/Constants/Common';
import {defaultCol} from '../../../../../components/Table/Interface/ITable';
import ActionsButton from '../../../../../components/Table/Utils/ActionsButton';
import {returnProductFilter} from '../../../../../components/Table/Utils/returnProductFilters';
import sumValue, {
  sumOfTimeFrames
} from '../../../../../components/Table/Utils/sumValue';
import {
  excludeAcceptedIdsForDelete,
  getBulkSelectedIdsInDataSource,
  useBulkCheckerModals,
  createBulkRowSelection
} from '../../../../../components/Table/Utils/TableBulkSelectionBar';
import TableTitleHeader from '../../../../../components/Table/Utils/TableTitleHeader';
import TimeFrameDataTooltip from '../../../../../components/Table/Utils/TimeFrameDataTooltip';
import {TF} from '../../../../../constants/Common';
import InformativeText from '../../../../../constants/InformativeText';
import {ISelect} from '../../../../../interface/ISelect';
import {APITypeEnum} from '../../../../../services/interface/tableAPIType';
import EndOfLifeAPI from '../../../../../services/modules/PRODUCTS/endOfLIfeAPI';
import productInfoAPI from '../../../../../services/modules/PRODUCTS/ProductInfoAPI';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../../../../store/platformStore/platformStore';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../../../../utils/commonFunctions';
import {formatIndian} from '../../../../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../../../../utils/SubscriptionHelper';

const EoLTTable: React.FC<{
  selectedYear: string;
}> = ({selectedYear}) => {
  interface DataType {
    _id: string;
    fuelType: number;
    noOfUnits: boolean;
    deviceType: number;
    fuelUsed: boolean;
    method: number;
    impacts: boolean;
    owned: boolean;
    quantity: number;
    cost: number;
    status?: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const bulkSelectedIds = useMemo(
    () => getBulkSelectedIdsInDataSource(selectedRowKeys, dataSource),
    [selectedRowKeys, dataSource]
  );
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [disposalModalData, setDisposalModalData] = useState([]);
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });

  const {products} = useJSONStore.getState();
  const eoltInfo = InformativeText?.Product?.eolt;
  const [rawMaterialFilter, setRawMaterialFilter] = useState<any>({});
  const [formRawMaterialFilter, setFormRawMaterialFilter] = useState<any>({});
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formSelectedYear, setFormSelectedYear] = useState('');
  const companyId = useCompanyStore.getState().companyId;
  const facilityDetails = useMemo(() => {
    return (
      useCompanyStore
        .getState()
        .facilityDetails?.[selectedYear ?? '']?.map((item: any) => {
          return {value: item._id, label: item?.[FacilityKey.FACILITY_NAME]};
        }) ?? []
    );
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      EndOfLifeAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      setDataSource([]);
    }
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        EndOfLifeAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        ),
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        )
      ])
        .then(([res1, res2]) => {
          setDataSource(res1 ?? []);
          setRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: EoLTKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: EoLTKey.FACILITIES,
                  filterer: facilityDetails,
                  subKey: EoLTKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                }
              ],
              res2,
              {filterKey: EoLTKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    if (formSelectedYear) {
      Promise.all([
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${formSelectedYear}`
        )
      ])
        .then(([res2]) => {
          const facilityDet =
            useCompanyStore
              .getState()
              .facilityDetails?.[formSelectedYear ?? '']?.map((item: any) => {
                return {
                  value: item._id,
                  label: item?.[FacilityKey.FACILITY_NAME]
                };
              }) ?? [];

          setFormRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: EoLTKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: EoLTKey.FACILITIES,
                  filterer: facilityDet,
                  subKey: EoLTKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                }
              ],
              res2,
              {filterKey: EoLTKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setIsFormLoading(false);
        });
    }
  }, [formSelectedYear]);

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];
    const endTreatment = excludeAcceptedIdsForDelete(ids, dataSource);
    if (endTreatment.length === 0) {
      return;
    }
    EndOfLifeAPI(APITypeEnum.DELETE_ALL, {
      endTreatment
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  const bulkChecker = useBulkCheckerModals({
    selectedIds: bulkSelectedIds,
    refresh,
    selectionContextKey: selectedYear,
    onSelectionContextChange: () => {
      setSelectedRowKeys([]);
    },
    getBulkDeleteEligibleCount: (ids) =>
      excludeAcceptedIdsForDelete(ids, dataSource).length,
    onSuccess: () => {
      setSelectedRowKeys([]);
      setRefresh(true);
    },
    onBulkStatusSubmit: async ({ids, status, comment}) =>
      await EndOfLifeAPI(APITypeEnum.STATUS_ALL, {
        endTreatment: ids,
        status,
        [checkerStatusKey.COMMENT]: comment
      }),
    onBulkDelete: async (ids) => {
      const endTreatment = excludeAcceptedIdsForDelete(ids, dataSource);
      if (endTreatment.length === 0) {
        return false;
      }

      return await EndOfLifeAPI(APITypeEnum.DELETE_ALL, {
        endTreatment
      });
    }
  });

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const {productFilter, facilityFilter} = rawMaterialFilter;

  const endLifeCycleKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: EoLTKey.PRODUCT_ID,
        label: EoLTLabel.PRODUCT_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter.productFilter
          : rawMaterialFilter.productFilter
      },
      {
        key: EoLTKey.FACILITY,
        label: EoLTLabel.FACILITY,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter.facilityFilter
          : rawMaterialFilter.facilityFilter
      },
      {
        key: TF,
        label: [EoLTLabel.QUANTITY_SOLD],
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      {
        key: EoLTKey.disposalMethodData,
        mainLabel: [
          EoLTLabel.disposalKey.landfill,
          EoLTLabel.disposalKey.incineration,
          EoLTLabel.disposalKey.recycling,
          EoLTLabel.disposalKey.reclaimed,
          EoLTLabel.disposalKey.reused,
          EoLTLabel.disposalKey.notTreated
        ],
        label: [EoLTLabel.treated.productTreated],
        tfKey: [EoLTKey.treated.productTreated],
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              key: checkerStatusKey.STATUS,
              label: checkerStatusLabel.STATUS,
              type: objTypes.status
            },
            {
              key: checkerStatusKey.COMMENT,
              label: checkerStatusLabel.COMMENT,
              type: objTypes.comment
            }
          ]
        : [])
    ];
  }, [formRawMaterialFilter, rawMaterialFilter]);

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = useMemo(() => {
    return [
      {
        title: EoLTLabel.PRODUCT_ID,
        dataIndex: EoLTKey.PRODUCT_ID,
        editable: true,
        type: 'select',
        ...SearchFilter(
          EoLTLabel.PRODUCT_ID,
          EoLTKey.PRODUCT_ID,
          productFilter
        ),
        render: (value) => {
          return (
            <>
              {
                productFilter?.find((item: any) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: EoLTLabel.FACILITY,
        dataIndex: EoLTKey.FACILITY,
        editable: true,
        type: 'select',
        render: (value) => {
          return (
            <>
              {
                facilityFilter?.find((item: any) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: EoLTLabel.QUANTITY_SOLD,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={WasteKey.wasteProduced} />
        //   );
        // }
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === EoLTKey.QUANTITY_SOLD;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${EoLTLabel.QUANTITY_SOLD} (${mainData?.unit})`,
                  dataKey: EoLTKey.QUANTITY_SOLD
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(
                formatIndian(sumValue(mainData?.data))
              )} ${mainData?.unit}`}</div>
            </div>
          );
        }
      },
      {
        title: EoLTLabel.treated.productTreated,
        dataIndex: EoLTKey.disposalMethodData,
        editable: true,
        type: 'select',
        render: (value) => {
          const filtered = value.filter((item: any) => {
            return (
              !!item.disposalMethod && item.disposalMethod !== 'notTreated'
            );
          });
          const productTreatedUnit = filtered?.[0]?.timeFrameData?.find(
            (item: any) => item?.type === EoLTKey.treated.productTreated
          )?.unit;

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setDisposalModalData(filtered);
                setOpenDisplayModalType({
                  title: `${EoLTLabel.treated.productTreated} (${
                    productTreatedUnit ?? ''
                  })`,
                  dataKey: EoLTKey.treated.productTreated
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${sumOfTimeFrames(
                filtered,
                EoLTKey.treated.productTreated
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: EoLTLabel.notTreated.ProductNotTreated,
        dataIndex: EoLTKey.disposalMethodData,
        editable: true,
        type: 'select',
        render: (value) => {
          const filtered = value.filter((item: any) => {
            return (
              !!item.disposalMethod && item.disposalMethod === 'notTreated'
            );
          })?.[0]?.[TF]?.[0];

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(filtered);
                setOpenDisplayModalType({
                  title: `${EoLTLabel.notTreated.ProductNotTreated} (${
                    filtered?.unit ?? ''
                  })`,
                  dataKey: ''
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(filtered?.data)))} ${
                filtered?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: RawMaterialLabel.STATUS_COMMENTS,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment">- {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      // ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      //   ? [
      //       {
      //         title: checkerStatusLabel.DATA_SOURCE,
      //         dataIndex: checkerStatusKey.DATA_SOURCE,
      //         editable: true,
      //         type: 'select',
      //         render: (value: any) => {
      //           return (
      //             <div>
      //               {value === checkerStatusKey.MANUAL
      //                 ? checkerStatusLabel.MANUAL
      //                 : checkerStatusLabel.BULK_UPLOAD}
      //             </div>
      //           );
      //         }
      //       }
      //     ]
      //   : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: RawMaterialLabel.STATUS,
              dataIndex: 'status',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockReadOnly()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'fullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <div>
      <TableTitleHeader
        title="End of life treatment info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Sold Product'
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs, uploadType: ''}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        tooltip={eoltInfo?.main}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
        onSelectAll={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? handleSelectAll
            : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        onChangeStatus={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openChangeStatus
            : undefined
        }
        onBulkDelete={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openBulkDelete
            : undefined
        }
      />
      {bulkChecker.bulkCheckerModals}

      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
      />
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          },
          enabled:
            hasUserRole(UserRole.Maker_Role) ||
            hasUserRole(UserRole.Both_role)
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
          setFormSelectedYear('');
        }}
        setRefresh={setRefresh}
        API={EndOfLifeAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <EoLTForm
            rawMaterialFilter={
              formSelectedYear ? formRawMaterialFilter : rawMaterialFilter
            }
            setFormSelectedYear={setFormSelectedYear}
            setIsFormLoading={setIsFormLoading}
            isFormLoading={isFormLoading}
          />
        }
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        onlyYear={true}
        stepsTitle={['Sold Product Details', 'Quantity Details']}
        formPreview={endLifeCycleKeyLabel}
      />
      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          [
            EoLTLabel.QUANTITY_SOLD,
            EoLTLabel.notTreated.ProductNotTreated
          ].some((label) => openDisplayModalType.title.includes(label)) ? (
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          ) : (
            <DisposalDisplay
              disposalModalData={disposalModalData}
              disposalModalType={openDisplayModalType.dataKey}
              disposalKey={EoLTKey.disposalMethod}
            />
          )
        }
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={endLifeCycleKeyLabel} />
        }
        API={EndOfLifeAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['All Data']}
      />
    </div>
  );
};

export default EoLTTable;
